import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';

import GetCookie from '../../utils/GetCookie';
import MarkdownComponents from '../../utils/MarkdownComponents';

import GraphComponent from './GraphComponent';
import TableComponent from './TableComponent';
import ImageComponent from './ImageComponent';
import ReferencesComponent from './ReferencesComponent';

/* ── Inline citation replacement ───────── */
const replaceInlineCitations = (text, references, citationMap) => {
  if (!text) return text;
  const docNameMap = {};
  if (citationMap && typeof citationMap === 'object') {
    for (const [id, name] of Object.entries(citationMap)) {
      docNameMap[parseInt(id, 10)] = name;
    }
  }
  if (references && references.length > 0) {
    for (const ref of references) {
      if (ref.doc_id && ref.doc_name) {
        docNameMap[ref.doc_id] = ref.doc_name;
      }
    }
  }
  if (Object.keys(docNameMap).length === 0) return text;
  return text.replace(/\[doc_id_(\d+)\]/g, (match, idStr) => {
    const docId = parseInt(idStr, 10);
    const docName = docNameMap[docId];
    if (!docName) return '';  
    return `<span class="ev-message-card6 m-0 mx-1 ev-btn-xsmall ev-bold-reg">${docName}</span>`;
  });
};

const TOOL_LABELS = {
  'Document Search':        { label: 'Text search',   icon: 'bi-file-text' },
  'Data Analysis (Python)': { label: 'Data analysis',  icon: 'bi-bar-chart' },
  'Image Analysis':         { label: 'Image analysis', icon: 'bi-image' },
  'Excel Adaptation':       { label: 'Excel',          icon: 'bi-file-earmark-spreadsheet' },
  'Excel Generation':       { label: 'Excel',          icon: 'bi-file-earmark-spreadsheet' },
};

const getToolsUsed = (thinkingLogs) => {
  if (!thinkingLogs?.steps) return [];
  const seen = new Set();
  for (const step of thinkingLogs.steps) {
    for (const te of step.tool_executions || []) {
      if (te.tool && te.status !== 'error') seen.add(te.tool);
    }
  }
  return Array.from(seen);
};

/* ── Defaults ────────────────────────────────────────────────────────── */
const DEFAULT_SUGGESTIONS = [
  { label: 'Disease burden', prompt: 'Give me highlights on disease burden' },
  { label: 'Efficacy evidence', prompt: 'What evidence supports the efficacy claims?' },
  { label: 'Treatment options', prompt: 'Give me a table of treatment options available' },
];

const DEFAULT_WIDTH_PCT = 36;
const MIN_WIDTH_PCT = 25;
const MAX_WIDTH_PCT = 60;
const MIN_PANEL_PX = 320;
const MINIMIZED_WIDTH = 380;

/* ── Component ───────────────────────────────────────────────────────── */
const GenieChatModal = ({
  isOpen,
  onClose,
  discoveryId,
  fabricId,
  threadId,
  docIds = [],
  paperCount = 0,
  suggestions = DEFAULT_SUGGESTIONS,
}) => {
  const [viewMode, setViewMode] = useState('expanded'); // 'expanded' | 'minimized'
  const [copiedId, setCopiedId] = useState(null);
  const [panelWidthPct, setPanelWidthPct] = useState(DEFAULT_WIDTH_PCT);
  const [minimizedPos, setMinimizedPos] = useState(null);
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );

  const [conversation, setConversation] = useState([]);
  const [question, setQuestion] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [isInitial, setIsInitial] = useState(true);

  const abortControllerRef = useRef(null);
  const messagesEndRef = useRef(null);
  const isDraggingRef = useRef(false);
  const minimizedDragRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (isOpen) scrollToBottom();
  }, [conversation, isOpen, scrollToBottom]);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
      document.removeEventListener('mousemove', handleMinimizedDragMove);
      document.removeEventListener('mouseup', handleMinimizedDragEnd);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── Minimized drag handlers ── */
  const handleMinimizedDragStart = (e) => {
    if (viewMode !== 'minimized') return;
    if (e.target.closest('button')) return;
    const panelEl = e.currentTarget.closest('.genie-minimized');
    if (!panelEl) return;
    const rect = panelEl.getBoundingClientRect();
    minimizedDragRef.current = { startX: e.clientX, startY: e.clientY, startLeft: rect.left, startTop: rect.top };
    document.body.classList.add('user-select-none');
    document.addEventListener('mousemove', handleMinimizedDragMove);
    document.addEventListener('mouseup', handleMinimizedDragEnd);
  };

  const handleMinimizedDragMove = (e) => {
    if (!minimizedDragRef.current) return;
    const { startX, startY, startLeft, startTop } = minimizedDragRef.current;
    let newLeft = Math.max(0, Math.min(window.innerWidth - MINIMIZED_WIDTH, startLeft + (e.clientX - startX)));
    let newTop = Math.max(0, Math.min(window.innerHeight - 60, startTop + (e.clientY - startY)));
    setMinimizedPos({ left: newLeft, top: newTop });
  };

  const handleMinimizedDragEnd = () => {
    minimizedDragRef.current = null;
    document.body.classList.remove('user-select-none');
    document.removeEventListener('mousemove', handleMinimizedDragMove);
    document.removeEventListener('mouseup', handleMinimizedDragEnd);
  };

  useEffect(() => {
    const handleResize = () => {
      const vw = window.innerWidth;
      setViewportWidth(vw);
      setMinimizedPos(prev => !prev ? prev : {
        left: Math.max(0, Math.min(vw - MINIMIZED_WIDTH, prev.left)),
        top: Math.max(0, Math.min(window.innerHeight - 60, prev.top)),
      });
      // Keep the expanded panel's pixel width within sane bounds as the window is resized
      setPanelWidthPct(prev => {
        const minPct = Math.min(MAX_WIDTH_PCT, (MIN_PANEL_PX / vw) * 100);
        return Math.max(MIN_WIDTH_PCT, Math.min(MAX_WIDTH_PCT, Math.max(prev, minPct)));
      });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  /* ── Left-edge resize handle ── */
  const handleEdgeResizeStart = (e) => {
    e.preventDefault();
    isDraggingRef.current = true;
    document.body.classList.add('user-select-none');

    const handleMove = (moveEvent) => {
      if (!isDraggingRef.current) return;
      const vw = window.innerWidth;
      const pct = ((vw - moveEvent.clientX) / vw) * 100;
      const minPct = Math.min(MAX_WIDTH_PCT, (MIN_PANEL_PX / vw) * 100);
      setPanelWidthPct(Math.max(MIN_WIDTH_PCT, minPct, Math.min(MAX_WIDTH_PCT, pct)));
    };

    const handleUp = () => {
      isDraggingRef.current = false;
      document.body.classList.remove('user-select-none');
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);
    };

    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleUp);
  };

  /* ── New Chat ── */
  const handleNewChat = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setConversation([]);
    setSessionId(null);
    setIsInitial(true);
    setIsGenerating(false);
    setQuestion('');
  };

  const handleStop = () => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
  };

  /* ── Send Question ── */
  const sendQuestion = async (inputText) => {
    if (!inputText || isGenerating) return;

    if (!docIds || docIds.length === 0) {
      setConversation((prev) => [
        ...prev,
        { text: inputText, isUser: true },
        {
          text: 'No documents are linked to this chat yet. Please close and reopen Ask Genie, or select at least one paper first.',
          isUser: false,
          timestamp: Date.now(),
          isErrorMessage: true,
        },
      ]);
      return;
    }

    if (abortControllerRef.current) abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();

    const originalQuestion = inputText;
    setQuestion('');
    setConversation((prev) => [...prev, { text: originalQuestion, isUser: true }]);

    const aiMessageIndex = Date.now();
    setConversation((prev) => [
      ...prev,
      {
        text: '',
        isUser: false,
        timestamp: aiMessageIndex,
        graphs: [],
        tables: [],
        images: [],
        references: null,
        steps: [],
        isStreaming: true,
      },
    ]);

    setIsGenerating(true);

    const updateMsg = (updater) => {
      setConversation((prev) =>
        prev.map((msg) => (msg.timestamp === aiMessageIndex && !msg.isUser ? updater(msg) : msg))
      );
    };

    try {
      const requestData = {
        question: inputText,
        is_new: isInitial,
        session_id: sessionId || null,
        doc_ids: docIds,
        fabric_id: fabricId || null,
        selected_capabilities: ['rag', 'image_analysis', 'python'],
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/pceapi/copilot/ask`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: GetCookie('access_token'),
        },
        body: JSON.stringify(requestData),
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        let detailMsg = `Server error: ${response.status}`;
        try {
          const errBody = await response.json();
          if (errBody?.detail) {
            detailMsg = Array.isArray(errBody.detail)
              ? errBody.detail.map((d) => d.msg || JSON.stringify(d)).join('; ')
              : String(errBody.detail);
          } else if (errBody?.message) {
            detailMsg = errBody.message;
          }
        } catch (_) {}
        throw new Error(detailMsg);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.trim() === '') continue;
          try {
            const data = JSON.parse(line);

            if (data.session) {
              setSessionId(data.session.session_id);
              setIsInitial(false);
            } else if (data.citation_map) {
              updateMsg((msg) => ({ ...msg, citationMap: data.citation_map }));
            } else if (data.markdown_chunk) {
              updateMsg((msg) => ({ ...msg, text: (msg.text || '') + data.markdown_chunk }));
            } else if (data.markdown) {
              updateMsg((msg) => ({ ...msg, text: data.markdown }));
            } else if (data.status_update) {
              const updateType = data.status_update.type;
              if (updateType === 'thinking') {
                const thinkingContent = data.status_update.content || '';
                updateMsg((msg) => {
                  const prevSteps = [...(msg.steps || [])];
                  const lastIdx = prevSteps.length - 1;
                  if (lastIdx >= 0) {
                    const lastStep = { ...prevSteps[lastIdx] };
                    lastStep.thinking = (lastStep.thinking || '') + thinkingContent;
                    prevSteps[lastIdx] = lastStep;
                  }
                  return { ...msg, steps: prevSteps };
                });
              } else {
                const statusMsg = data.status_update.message;
                const logLevel = data.status_update.log_level || 'phase';
                if (statusMsg) {
                  updateMsg((msg) => {
                    const prevSteps = [...(msg.steps || [])];
                    if (logLevel === 'detail') {
                      const lastPhaseIdx = prevSteps.map((s) => s.logLevel).lastIndexOf('phase');
                      if (lastPhaseIdx !== -1) {
                        const phaseStep = { ...prevSteps[lastPhaseIdx] };
                        phaseStep.details = [...(phaseStep.details || []), statusMsg];
                        prevSteps[lastPhaseIdx] = phaseStep;
                      }
                      return { ...msg, steps: prevSteps };
                    }
                    const completedSteps = prevSteps.map((s) => ({ ...s, status: 'completed' }));
                    return {
                      ...msg,
                      steps: [
                        ...completedSteps,
                        {
                          id: Date.now() + Math.random(),
                          message: statusMsg,
                          status: 'processing',
                          logLevel,
                          details: [],
                          timestamp: new Date().toISOString(),
                        },
                      ],
                    };
                  });
                }
              }
            } else if (data.alert) {
              if (data.alert.type === 'graph') {
                updateMsg((msg) => ({ ...msg, graphs: [...msg.graphs, { contentId: data.alert.content_id, isLoading: true, data: null }] }));
              } else if (data.alert.type === 'table') {
                updateMsg((msg) => ({ ...msg, tables: [...msg.tables, { contentId: data.alert.content_id, isLoading: true, data: null }] }));
              } else if (data.alert.type === 'image') {
                updateMsg((msg) => ({ ...msg, images: [...(msg.images || []), { contentId: data.alert.content_id, isLoading: true, data: null }] }));
              }
            } else if (data.graph) {
              updateMsg((msg) => ({
                ...msg,
                graphs: msg.graphs.map((g) => (g.contentId === data.graph.graph_id ? { ...g, isLoading: false, data: data.graph } : g)),
              }));
            } else if (data.table) {
              updateMsg((msg) => ({
                ...msg,
                tables: msg.tables.map((t) => (t.contentId === data.table.table_id ? { ...t, isLoading: false, data: data.table } : t)),
              }));
            } else if (data.image) {
              updateMsg((msg) => ({
                ...msg,
                images: (msg.images || []).map((img) => (img.contentId === data.image.image_id ? { ...img, isLoading: false, data: data.image } : img)),
              }));
            } else if (data.thinking_logs) {
              updateMsg((msg) => ({ ...msg, thinkingLogs: data.thinking_logs }));
            } else if (data.references) {
              updateMsg((msg) => ({ ...msg, references: data.references.content }));
            } else if (data.error) {
              if (data.error.content_type === 'graph') {
                updateMsg((msg) => ({
                  ...msg,
                  graphs: msg.graphs.map((g) => (g.contentId === data.error.content_id ? { ...g, isLoading: false, data: null } : g)),
                }));
              } else if (data.error.content_type === 'table') {
                updateMsg((msg) => ({
                  ...msg,
                  tables: msg.tables.map((t) => (t.contentId === data.error.content_id ? { ...t, isLoading: false, data: null } : t)),
                }));
              } else {
                throw new Error(data.error.message);
              }
            }
          } catch (parseError) {
            console.warn('[Genie] Unparseable stream line, skipped:', line.slice(0, 200));
          }
        }
      }

      updateMsg((msg) => ({ ...msg, isStreaming: false, steps: (msg.steps || []).map((s) => ({ ...s, status: 'completed' })) }));
    } catch (error) {
        if (error.name === 'AbortError') {
        updateMsg((msg) => ({ ...msg, isStreaming: false, wasStopped: true }));
      } else {
        setConversation((prev) => prev.slice(0, -1));
        setConversation((prev) => [
          ...prev,
          {
            text: `${error.message || 'Failed to get an answer. Please try again.'}`,
            isUser: false,
            timestamp: Date.now(),
            isErrorMessage: true,
          },
        ]);
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSend = () => sendQuestion(question);
  const handleSuggestionClick = (s) => sendQuestion(s.prompt || s.label);
  const handleCopy = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text || '');
      setCopiedId(id);
      setTimeout(() => setCopiedId((prev) => (prev === id ? null : prev)), 1500);
    } catch (e) {
      console.error('Copy failed', e);
    }
  };
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && question && !isGenerating) handleSend();
  };

  if (!isOpen) return null;

  /* ── Shared: messages ── */
  const renderMessages = () => (
    <div className="flex-grow-1 px-3 py-2 scrollable-element">
      {conversation.length === 0 ? (
        <div className="d-flex flex-column align-items-center justify-content-center h-100 text-center p-4">
          <i className="bi bi-stars ev-fs-30 gradient-font6 mb-2"></i>
          <p className="ev-reg-p-dark ev-bold-med mb-1">Ask Genie about these papers</p>
          <p className="ev-small-p-blue mb-0">Pick a topic above, or type your own question below.</p>
        </div>
      ) : (
        conversation.map((message, idx) => {
          if (message.isUser) {
            return (
              <div key={idx} className="mb-3 d-flex justify-content-end">
                <span className="chat-message user ev-radius-reg ev-small-p-dark text-start">
                  <i className="bi bi-person-fill chat-icon"></i> You.{message.text}
                </span>
              </div>
            );
          }

          const isStillThinking = message.isStreaming;
          const referencedDocCount = Array.isArray(message.references?.doc_ids)
            ? message.references.doc_ids.length
            : Array.isArray(message.references) ? message.references.length : 0;
          const toolsUsed = getToolsUsed(message.thinkingLogs);

          return (
            <div key={message.timestamp || idx} className="mb-3">
              <div className="d-flex align-items-center gap-2 mb-1">
                <i className="bi bi-stars gradient-font5 ev-fs-15"></i>
                <span className="ev-reg-p-dark ev-bold-med">Genie</span>
                {message.text && !message.isStreaming && (
                  <button type="button" className="ev-btn-light-fill ev-btn-xsmall ev-radius-xsmall border-0 ms-auto"
                    onClick={() => handleCopy(message.text, message.timestamp)}
                    title="Copy response">
                    <i className={`bi ${copiedId === message.timestamp ? 'bi-check-lg' : 'bi-clipboard'}`}></i>
                    {copiedId === message.timestamp ? ' Copied' : ''}
                  </button>
                )}
              </div>

              {isStillThinking && (
                <span className="gradient-font5 ev-bold-reg ev-reg-p-dark d-inline-flex align-items-center gap-1 mb-2">
                  <i className="bi bi-stars gradient-font5 ev-fs-14"></i>
                  Thinking
                  <img src="media/loader2.svg" className="w-8" alt="" />
                </span>
              )}
              {!isStillThinking && !message.text ? null : (
                <>
                  {message.text && (
                    <div className={`ev-radius-reg ev-padding-small ev-reg-p-dark markdown-justified p-3 ${message.isErrorMessage ? 'ev-alert-card-warning' : 'ev-card-2'}`}>
                      <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]} components={MarkdownComponents}>
                        {replaceInlineCitations(message.text, message.references, message.citationMap)}
                      </ReactMarkdown>
                    </div>
                  )}
                  {message.graphs?.map((graph) => (
                    <GraphComponent key={graph.contentId} graphData={graph.data} isLoading={graph.isLoading} contentId={graph.contentId} />
                  ))}
                  {message.tables?.map((table) => (
                    <TableComponent key={table.contentId} tableData={table.data} isLoading={table.isLoading} contentId={table.contentId} />
                  ))}
                  {message.images?.map((img, imgIdx) => (
                    <ImageComponent key={img.contentId} imageData={img.data} isLoading={img.isLoading} contentId={imgIdx + 1} subtitle={img.data?.subtitle || ''} footer={img.data?.footer || null} />
                  ))}
                  {toolsUsed.length > 0 && (
                    <div className="d-flex flex-wrap gap-1 mt-1">
                      {toolsUsed.map((t) => {
                        const meta = TOOL_LABELS[t] || { label: t, icon: 'bi-gear' };
                        return (
                          <span key={t} className="ev-message-card3 ev-btn-xsmall">
                            <i className={`bi ${meta.icon} me-1`}></i>{meta.label}
                          </span>
                        );
                      })}
                    </div>
                  )}
                  {message.references && referencedDocCount > 0 && (
                    <p className="ev-xsmall-p-dark mt-1 mb-0">Based on {referencedDocCount} paper{referencedDocCount !== 1 ? 's' : ''}</p>
                  )}
                  {message.references && <ReferencesComponent references={message.references} />}
                </>
              )}
            </div>
          );
        })
      )}
      <div ref={messagesEndRef} />
    </div>
  );

  /* ── Shared: input bar ── */
  const renderInput = () => (
    <div className="d-flex align-items-center gap-2 px-3 py-2 border-top flex-shrink-0 ev-card-3">
      <input
        type="text"
        className="formField6 ev-radius-xlarge flex-grow-1"
        placeholder="Ask me anything about these papers..."
        value={question}
        onChange={(e) => setQuestion(e.target.value)}
        onKeyDown={handleKeyPress}
        disabled={isGenerating}
      />
      <button
        type="button"
        className={isGenerating ? "ev-btn-red-fill-round ev-btn-small ev-radius-xlarge flex-shrink-0" : "ev-btn-gradient-fill-round ev-btn-small ev-radius-xlarge flex-shrink-0"}
        onClick={isGenerating ? handleStop : handleSend}
        disabled={!isGenerating && !question}
        title={isGenerating ? "Stop generating" : "Send"}
      >
        {isGenerating ? <i className="bi bi-stop-fill"></i> : <i className="bi bi-arrow-up"></i>}
      </button>
    </div>
  );

  /* ── Shared: suggestion chips ── */
  const renderChips = () => conversation.length === 0 && suggestions?.length > 0 && (
    <div className="d-flex flex-wrap gap-2 px-3 py-2 border-bottom flex-shrink-0">
      {suggestions.map((s) => (
        <button key={s.label} type="button" className="ev-btn-blue-outline-round ev-btn-xsmall ev-bold-reg"
          onClick={() => handleSuggestionClick(s)} disabled={isGenerating}>
          {s.label}
        </button>
      ))}
    </div>
  );

  /* ── Shared: header ── */
  const renderHeader = (draggable = false) => (
    <div
      className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom flex-shrink-0"
      style={draggable ? { cursor: 'move' } : undefined}
      onMouseDown={draggable ? handleMinimizedDragStart : undefined}
    >
      <div className="d-flex align-items-center gap-2">
        <i className="bi bi-stars gradient-font5 ev-fs-16"></i>
        <span className="ev-reg-p-dark-blue ev-bold-med">Genie.ai</span>
        {paperCount > 0 && (
          <span className="ev-message-card3 ev-btn-xsmall">
            <i className="bi bi-file-earmark-text me-1"></i>{paperCount} papers
          </span>
        )}
      </div>
      <div className="d-flex align-items-center gap-1">
        <button type="button" className="ev-btn-light-fill ev-btn-xsmall ev-radius-xsmall border-0" onClick={handleNewChat} title="New chat">
          <i className="bi bi-arrow-clockwise"></i>
        </button>
        <button type="button" className="ev-btn-light-fill ev-btn-xsmall ev-radius-xsmall border-0"
          onClick={() => setViewMode(v => v === 'expanded' ? 'minimized' : 'expanded')}
          title={viewMode === 'expanded' ? 'Minimize' : 'Expand'}>
          <i className={`bi ${viewMode === 'expanded' ? 'bi-dash-lg' : 'bi-arrows-angle-expand'}`}></i>
        </button>
        <button type="button" className="ev-btn-light-fill ev-btn-xsmall ev-radius-xsmall border-0" onClick={onClose} aria-label="Close">
          <i className="bi bi-x-lg"></i>
        </button>
      </div>
    </div>
  );

  /* ── MINIMIZED: draggable floating window (position:fixed, viewport-relative) ── */
  if (viewMode === 'minimized') {
    const minStyle = minimizedPos
      ? { left: minimizedPos.left, top: minimizedPos.top, right: 'auto', bottom: 'auto', width: MINIMIZED_WIDTH, zIndex: 1050 }
      : { right: 24, bottom: 80, width: MINIMIZED_WIDTH, zIndex: 1050 };
    return (
      <div className="genie-minimized position-fixed ev-card-4 d-flex flex-column overflow-hidden p-0"
        style={{ ...minStyle, height: 480, maxHeight: 'calc(100vh - 24px)', borderRadius: 12 }}>
        {renderHeader(true)}
        {renderChips()}
        {renderMessages()}
        {renderInput()}
      </div>
    );
  }

  /* ── EXPANDED: position:fixed to the viewport so it always renders correctly
        regardless of ancestor layout/scroll. Insets are in vh (not px) so the
        panel's position/height stays visually consistent at any browser zoom
        level or screen size — a fixed px inset would look proportionally
        different once the page is zoomed in/out. ── */
  const expandedWidthStyle = { width: `${panelWidthPct}%`, minWidth: MIN_PANEL_PX, maxWidth: '90vw' };

  const EXPANDED_TOP_INSET = '8vh';
  const EXPANDED_BOTTOM_INSET = '4vh';

  return (
    <div
      className="position-fixed end-0 d-flex genie-expanded"
      style={{
        ...expandedWidthStyle,
        top: EXPANDED_TOP_INSET,
        bottom: EXPANDED_BOTTOM_INSET,
        zIndex: 1050,
      }}
    >
      {/* Left-edge resize handle */}
      <div className="flex-shrink-0" style={{ width: '6px', cursor: 'col-resize' }} onMouseDown={handleEdgeResizeStart} />

      {/* Panel body */}
      <div className="ev-card-4 d-flex flex-column p-0 flex-grow-1 overflow-hidden" style={{ minWidth: 0, borderRadius: 12, boxShadow: '0 8px 24px rgba(0,0,0,0.15)' }}>
        {renderHeader()}
        {renderChips()}
        {renderMessages()}
        {renderInput()}
      </div>
    </div>
  );
};

export default GenieChatModal;